import { forwardRef, type InputHTMLAttributes } from 'react';
import { cn } from '@/lib/format';

export interface ToggleProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'onChange'> {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label?: string;
  description?: string;
}

export const Toggle = forwardRef<HTMLInputElement, ToggleProps>(function Toggle(
  { checked, onChange, label, description, disabled, className, id, ...rest },
  ref,
) {
  const toggleId = id || `toggle-${Math.random().toString(36).slice(2, 9)}`;
  return (
    <label
      htmlFor={toggleId}
      className={cn(
        'flex items-start gap-3 cursor-pointer',
        disabled && 'opacity-50 cursor-not-allowed',
        className,
      )}
    >
      <div className="relative flex-shrink-0 mt-0.5">
        <input
          ref={ref}
          id={toggleId}
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(e.target.checked)}
          disabled={disabled}
          className="sr-only peer"
          {...rest}
        />
        <div
          className={cn(
            'w-10 h-6 rounded-full transition-colors',
            checked ? 'bg-primary' : 'bg-line',
            !disabled && 'peer-focus-visible:ring-2 peer-focus-visible:ring-primary/30 peer-focus-visible:ring-offset-1',
          )}
        />
        <div
          className={cn(
            'absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform',
            checked && 'translate-x-4',
          )}
        />
      </div>
      {(label || description) && (
        <div className="flex-1 min-w-0">
          {label && <p className="text-body font-medium text-ink">{label}</p>}
          {description && <p className="text-caption text-ink-subtle mt-0.5">{description}</p>}
        </div>
      )}
    </label>
  );
});
